// src/components/MenuComponent.tsx

import React, {
    useEffect,
    useState,
} from 'react';

import {
    Link,
    useNavigate,
} from 'react-router-dom';

import {
    Button,
    Group,
    Modal,
    Select,
    Stack,
} from '@mantine/core';

import { useLanguage } from '../context/LanguageContext';

import {
    IconCalendarEvent,
    IconClock,
    IconId,
    IconToolsKitchen3,
    IconFiles,
    IconMessages,
    IconHotelService,
    IconBus,
    IconBarbell,
    IconFileText,
    IconPhoto,
    IconBuilding,
    IconPalette,
    IconUserCog,
    IconUsers,
    IconUserCheck,
    IconQrcode,
    IconParking,
    IconGlass,
    IconSparkles,
    IconChartBar,
} from '@tabler/icons-react';

import './MenuComponent.css';


/* =========================================
   TYPES
   ========================================= */

type VisibleOn = 'admin' | 'club' | 'event';

type MenuSection =
    | 'event'
    | 'players'
    | 'admin'
    | 'other';

interface MenuItem {
    id: number;
    titleEn: string;
    titleDa: string;
    link: string;
    icon: React.ReactNode;
    section: MenuSection;
    disabled?: boolean;
    visibleOn?: VisibleOn[];
}

interface MenuSectionConfig {
    id: MenuSection;
    titleDa: string;
    titleEn: string;
}

interface MenuComponentProps {
    itemsToShow?: number[];
}


/* =========================================
   SECTIONS
   ========================================= */

const menuSections: MenuSectionConfig[] = [
    {
        id: 'event',
        titleDa: 'Event',
        titleEn: 'Event',
    },
    {
        id: 'players',
        titleDa: 'Spillere & Teams',
        titleEn: 'Players & Teams',
    },
    {
        id: 'admin',
        titleDa: 'Administration',
        titleEn: 'Administration',
    },
    {
        id: 'other',
        titleDa: 'Øvrigt',
        titleEn: 'Other',
    },
];


/* =========================================
   MENU ITEMS
   ========================================= */

const defaultMenuItems: MenuItem[] = [

    /* =====================================
       EVENT
       ===================================== */

    {
        id: 1,
        titleEn: 'Events',
        titleDa: 'Events',
        icon: <IconCalendarEvent />,
        link: '/eventmenu',
        section: 'event',
        visibleOn: ['admin'],
    },

    {
        id: 2,
        titleEn: 'Shifts',
        titleDa: 'Vagt',
        icon: <IconClock />,
        link: '/shifts',
        section: 'event',
    },

    {
        id: 4,
        titleEn: 'ID',
        titleDa: 'ID',
        icon: <IconId />,
        link: '/id',
        section: 'event',
    },

    {
        id: 9,
        titleEn: 'Ticket',
        titleDa: 'Spisebillet',
        icon: <IconToolsKitchen3 />,
        link: '/ticket',
        section: 'event',
    },

    {
        id: 22,
        titleEn: 'Document System',
        titleDa: 'Dokumentsystem',
        icon: <IconFiles />,
        link: '/admin/events/files',
        section: 'event',
    },

    {
        id: 18,
        titleEn: 'Group Messages',
        titleDa: 'Gruppebeskeder',
        icon: <IconMessages />,
        link: '/gruppebeskeder',
        section: 'event',
    },

    {
        id: 10,
        titleEn: 'Meeting Rooms',
        titleDa: 'Mødelokaler',
        icon: <IconBuilding />,
        link: '/admin/MeetingAdmin',
        section: 'event',
    },



    /* =====================================
       SPILLERE & TEAMS
       ===================================== */

    {
        id: 5,
        titleEn: 'Hotel',
        titleDa: 'Hotel',
        icon: <IconHotelService />,
        link: '/admin/hotel',
        section: 'players',
    },

    {
        id: 11,
        titleEn: 'Transport',
        titleDa: 'Transport',
        icon: <IconBus />,
        link: '/transportDashboard',
        section: 'players',
    },

    {
        id: 7,
        titleEn: 'Training',
        titleDa: 'Træning',
        icon: <IconBarbell />,
        link: '/admin/Practice',
        section: 'players',
    },

    {
        id: 6,
        titleEn: 'Visa',
        titleDa: 'Visum',
        icon: <IconFileText />,
        link: '/visa',
        section: 'players',
    },

    {
        id: 12,
        titleEn: 'Media',
        titleDa: 'Media',
        icon: <IconPhoto />,
        link: '/admin/mediaapplication',
        section: 'other',
    },


    /* =====================================
       ADMINISTRATION
       ===================================== */

    {
        id: 14,
        titleEn: 'ID Designer',
        titleDa: 'ID Designer',
        icon: <IconPalette />,
        link: '/iddesigner',
        section: 'admin',
    },

    {
        id: 15,
        titleEn: 'User Admin',
        titleDa: 'Bruger Admin',
        icon: <IconUserCog />,
        link: '/useradmin',
        section: 'admin',
    },

    {
        id: 16,
        titleEn: 'Volunteer List',
        titleDa: 'Frivilligeliste',
        icon: <IconUsers />,
        link: '/friviligeliste',
        section: 'admin',
    },

    {
        id: 102,
        titleEn: 'Volunteer Groups',
        titleDa: 'Frivilliggrupper',
        icon: <IconUserCheck />,
        link: '/admin/volunteer-groups',
        section: 'admin',
    },

    {
        id: 100,
        titleEn: 'QR Card',
        titleDa: 'QR Kortet',
        icon: <IconQrcode />,
        link: '/admin/person-overview-access',
        section: 'admin',
    },

    {
        id: 101,
        titleEn: 'Parking',
        titleDa: 'Parkering',
        icon: <IconParking />,
        link: '/admin/transportVIP',
        section: 'admin',
    },

    {
        id: 17,
        titleEn: 'Shift Stats',
        titleDa: 'Vagt stats',
        icon: <IconChartBar />,
        link: '/useradmin',
        section: 'admin',
        disabled: true,
    },


    /* =====================================
       ØVRIGT
       ===================================== */

    {
        id: 50,
        titleEn: 'Experience',
        titleDa: 'Oplevelser',
        icon: <IconSparkles />,
        link: '/admin/Experience',
        section: 'other',
    },

    {
        id: 49,
        titleEn: 'Drinks Admin',
        titleDa: 'Drinks Admin',
        icon: <IconGlass />,
        link: '/DrinksMenu',
        section: 'other',
    },

    {
        id: 51,
        titleEn: 'Player Lounge Food',
        titleDa: 'Player Lounge Mad',
        icon: <IconToolsKitchen3 />,
        link: '/food',
        section: 'other',
    },
    {
        id: 10,
        titleEn: 'Umpire Payment',
        titleDa: 'Umpire Payment',
        icon: <IconBuilding />,
        link: '/admin/payment-requests',
        section: 'other',
    },
];


/* =========================================
   COMPONENT
   ========================================= */

const MenuComponent: React.FC<MenuComponentProps> = ({
    itemsToShow,
}) => {

    const { language } = useLanguage();
    const navigate = useNavigate();

    const [foodModalOpened, setFoodModalOpened] =
        useState(false);

    const [selectedFoodEvent, setSelectedFoodEvent] =
        useState<string | null>(null);

    const [events, setEvents] =
        useState<
            {
                eventID: number;
                eventName: string;
            }[]
        >([]);

    const filteredMenuItems = itemsToShow
        ? defaultMenuItems.filter(
            (item) => itemsToShow.includes(item.id)
        )
        : defaultMenuItems;


    // =========================================
    // LOAD EVENTS FOR FOOD MODAL
    // =========================================

    useEffect(() => {

        const loadEvents = async () => {

            try {

                const apiBase =
                    process.env.REACT_APP_API_URL;

                const token =
                    sessionStorage.getItem(
                        'authToken'
                    );

                const res = await fetch(
                    `${apiBase}/api/events`,
                    {
                        headers: token
                            ? {
                                Authorization:
                                    `Bearer ${token}`,
                            }
                            : {},
                    }
                );

                if (!res.ok) {

                    console.error(
                        'Kunne ikke hente events.'
                    );

                    return;
                }

                const data = await res.json();

                setEvents(
                    Array.isArray(data)
                        ? data
                        : []
                );

            } catch (error) {

                console.error(
                    'Kunne ikke hente events:',
                    error
                );
            }
        };

        loadEvents();

    }, []);


    return (
    <>

            <Modal
                opened={foodModalOpened}
                onClose={() => {
                    setFoodModalOpened(false);
                    setSelectedFoodEvent(null);
                }}
                title="Player Lounge Food"
                centered
                size="sm"
            >

                <Stack gap="md">

                    <Select
                        label={
                            language === 'da'
                                ? 'Vælg event'
                                : 'Select event'
                        }
                        placeholder={
                            language === 'da'
                                ? 'Vælg event'
                                : 'Select event'
                        }
                        value={selectedFoodEvent}
                        onChange={setSelectedFoodEvent}
                        data={events.map(
                            (event) => ({
                                value:
                                    String(
                                        event.eventID
                                    ),
                                label:
                                    event.eventName,
                            })
                        )}
                        searchable
                    />

                    <Group justify="flex-end">

                        <Button
                            variant="default"
                            onClick={() => {
                                setFoodModalOpened(false);
                                setSelectedFoodEvent(null);
                            }}
                        >
                            {language === 'da'
                                ? 'Annuller'
                                : 'Cancel'}
                        </Button>

                        <Button
                            disabled={
                                !selectedFoodEvent
                            }
                            onClick={() => {

                                if (
                                    !selectedFoodEvent
                                ) {
                                    return;
                                }

                                setFoodModalOpened(
                                    false
                                );

                                navigate(
                                    `/food/${selectedFoodEvent}`
                                );
                            }}
                        >
                            {language === 'da'
                                ? 'Åbn'
                                : 'Open'}
                        </Button>

                    </Group>

                </Stack>

            </Modal>


            <div className="menu-container">

            {menuSections.map((section) => {

                const sectionItems =
                    filteredMenuItems.filter(
                        (item) =>
                            item.section === section.id
                    );


                // Vis ikke tomme sektioner
                if (sectionItems.length === 0) {
                    return null;
                }


                return (
                    <section
                        className="menu-section"
                        key={section.id}
                    >

                        {/* HEADER */}

                        <div className="menu-section-header">

                            <h2 className="menu-section-title">
                                {language === 'da'
                                    ? section.titleDa
                                    : section.titleEn}
                            </h2>

                            <span className="menu-section-count">
                                {sectionItems.length}
                            </span>

                        </div>


                        {/* MENU */}

                        <div className="menu-grid">

                            {sectionItems.map((item) => {

                                const title =
                                    language === 'da'
                                        ? item.titleDa
                                        : item.titleEn;


                                /* =====================
                                   DISABLED
                                   ===================== */

                                if (item.disabled) {

                                    return (
                                        <div
                                            key={item.id}
                                            className="menu-card disabled"
                                        >

                                            <div className="menu-card-content">

                                                <div className="menu-icon">
                                                    {item.icon}
                                                </div>

                                                <div className="menu-card-text">

                                                    <div className="menu-title">
                                                        {title}
                                                    </div>

                                                    <div className="menu-disabled-text">
                                                        {language === 'da'
                                                            ? 'Kommer snart'
                                                            : 'Coming soon'}
                                                    </div>

                                                </div>

                                            </div>

                                        </div>
                                    );
                                }

                                /* =====================
                                   PLAYER LOUNGE FOOD
                                   ===================== */

                                if (item.id === 51) {

                                    return (
                                        <div
                                            key={item.id}
                                            className="menu-card"
                                            onClick={() =>
                                                setFoodModalOpened(true)
                                            }
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                        >

                                            <div className="menu-card-content">

                                                <div className="menu-icon">
                                                    {item.icon}
                                                </div>

                                                <div className="menu-card-text">

                                                    <div className="menu-title">
                                                        {title}
                                                    </div>

                                                </div>

                                            </div>

                                        </div>
                                    );
                                }

                                /* =====================
                                   NORMAL MENU ITEM
                                   ===================== */

                                return (
                                    <Link
                                        key={item.id}
                                        to={item.link}
                                        className="menu-card"
                                    >

                                        <div className="menu-card-content">

                                            <div className="menu-icon">
                                                {item.icon}
                                            </div>

                                            <div className="menu-card-text">

                                                <div className="menu-title">
                                                    {title}
                                                </div>

                                            </div>

                                        </div>

                                    </Link>
                                );
                            })}

                        </div>

                    </section>
                );
            })}

            </div>

        </>
    );
};

export default MenuComponent;